import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface BusinessBranding {
  id: string;
  tenantId: string;
  logoUrl: string | null;
  /** Legacy tenant-wide receipt logo. No longer written: the receipt logo is
   *  location-specific now — see useLocationLogo. */
  receiptLogoUrl: string | null;
  faviconUrl: string | null;
  businessTitle: string | null;
  businessDescription: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useBusinessBranding(tenantId: string) {
  return useQuery<{ data: BusinessBranding | null }>({
    queryKey: ['business-branding', tenantId],
    queryFn: () => api.get(`/admin/branding/${tenantId}`),
    enabled: !!tenantId,
  });
}

export function useSaveBusinessBranding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { tenantId: string; businessTitle?: string; businessDescription?: string }) =>
      api.put(`/admin/branding/${payload.tenantId}`, {
        businessTitle: payload.businessTitle,
        businessDescription: payload.businessDescription,
      }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['business-branding', variables.tenantId] });
    },
  });
}

export function useUploadLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, file }: { tenantId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`/api/v1/admin/branding/${tenantId}/upload-logo`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
        throw error;
      }
      return response.json();
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['business-branding', variables.tenantId] });
    },
  });
}

export function useUploadFavicon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, file }: { tenantId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`/api/v1/admin/branding/${tenantId}/upload-favicon`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
        throw error;
      }
      return response.json();
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['business-branding', variables.tenantId] });
    },
  });
}
